import { useState } from 'react';

type ConfirmationCodeProps = {
  label: string;
  code: string;
  hint?: string;
};

// Card-style confirmation code: uppercase label, per-digit boxes, copy button,
// and an optional hint — matching the Clorox/Tap reference design.
export function ConfirmationCode({ label, code, hint }: ConfirmationCodeProps) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard
      ?.writeText(code)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };

  return (
    <div className="mx-auto mt-6 mb-1 w-full max-w-[340px] bg-paper border border-rule rounded-2xl p-5 text-center">
      <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-muted mb-3">
        {label}
      </div>

      <div className="flex justify-center gap-2 mb-3.5">
        {code.split('').map((ch, i) => (
          <div
            key={i}
            className="w-[46px] h-[58px] rounded-xl bg-surface border-[1.5px] border-rule flex items-center justify-center font-mono font-bold text-[28px] text-ink"
            style={{
              animation: 'popIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both',
              animationDelay: `${i * 0.05}s`,
            }}
          >
            {ch}
          </div>
        ))}
      </div>

      <button
        data-testid="confirmation-code-copy-button"
        onClick={copy}
        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-primary px-3 py-1.5 rounded-lg hover:bg-primary-pale transition-colors"
      >
        <svg
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h6a2 2 0 002-2v-2"
          />
        </svg>
        {copied ? 'Copied' : 'Copy code'}
      </button>

      {hint && (
        <p className="text-[13px] text-ink-muted mt-2.5 leading-[1.5] max-w-[360px] mx-auto">
          {hint}
        </p>
      )}
    </div>
  );
}
